import { useMemo, useState } from "react";

import { Link } from "@tanstack/react-router";

import { Banner, Button, Field, Input } from "@/components/ui";

import { MarketingShell } from "./MarketingShell";

/** The two sold tiers. `partner_reach` and `comp` are admin grants and never appear here. */
const TIERS = [
  {
    id: "standard",
    name: "Standard",
    participants: 200,
    capabilities: ["chat", "gaze", "effector.obs", "rooms", "channel.points"],
  },
  {
    id: "partner",
    name: "Partner",
    participants: 2000,
    capabilities: ["chat", "gaze", "effector.obs", "rooms", "channel.points", "clips"],
  },
] as const;

/** Quotas carrying the same number on every tier, so no column claims they differ. */
const EVERY_PLAN = [
  ["Spaces", "3"],
  ["Concurrent activities", "8"],
  ["Fully scoped canvases per activity", "50"],
  ["Shared canvases per activity", "16"],
  ["Keyed canvas keys per activity", "200"],
  ["Handler invocations per activity", "200,000"],
  ["Moderators per channel", "5"],
  ["Paired machines", "2"],
  ["Activity duration", "12 hours"],
  ["Asset storage", "2,000 MB"],
] as const;

const LOW_RATE = 0.1;
const HIGH_RATE = 0.25;

const NUMBER = new Intl.NumberFormat();

/** A value the platform has not published yet, rendered as itself rather than as a blank. */
function Unset({ what }: { what: string }) {
  return <span className="mk-unset">{what}</span>;
}

function tierFor(participants: number): string {
  const found = TIERS.find((tier) => participants <= tier.participants);
  return found === undefined ? "Above every tier" : found.name;
}

function Estimator() {
  const [viewers, setViewers] = useState("");
  const parsed = Number(viewers);
  const valid = viewers.trim() !== "" && Number.isFinite(parsed) && parsed > 0;

  const result = useMemo(() => {
    if (!valid) return "";
    const low = Math.max(1, Math.round(parsed * LOW_RATE));
    const high = Math.max(1, Math.round(parsed * HIGH_RATE));
    return `${NUMBER.format(low)} to ${NUMBER.format(high)} concurrent participants. ${tierFor(high)}.`;
  }, [parsed, valid]);

  return (
    <div className="mk-est">
      <Field label="Concurrent viewers">
        <Input
          type="number"
          min={1}
          inputMode="numeric"
          value={viewers}
          onChange={(event) => setViewers(event.target.value)}
        />
      </Field>
      <p className="mk-est-out" role="status" aria-live="polite">
        {result === "" ? <span className="mk-hint">Enter a number.</span> : <strong>{result}</strong>}
      </p>
    </div>
  );
}

/** The pricing page, per `docs/ui/marketing/pricing.md`. No free tier, and no price exists yet. */
export function Pricing() {
  return (
    <MarketingShell>
      <section className="mk-page">
        <p className="mk-eyebrow">Pricing</p>
        <h1 className="mk-h1">Priced by how many people play at once</h1>
        <p className="mk-lede">
          Plans are tiered by concurrent participants. Streaming is a paid product and there is no
          free tier.
        </p>
      </section>

      <section className="mk-section">
        <Banner tone="info" title="Prices are not published yet" className="mk-gap">
          Every ceiling and capability below is what the platform enforces today. The amounts are
          not set.
        </Banner>

        <div className="mk-table-wrap">
          <table className="mk-table">
            <caption>What each tier carries</caption>
            <thead>
              <tr>
                <th scope="col">
                  <span className="mk-hint">Tier</span>
                </th>
                {TIERS.map((tier) => (
                  <th key={tier.id} scope="col">
                    {tier.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Concurrent participants</th>
                {TIERS.map((tier) => (
                  <td key={tier.id}>
                    <span className="mk-ceiling">{NUMBER.format(tier.participants)}</span>
                    <span className="mk-unit">at once</span>
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Price</th>
                {TIERS.map((tier) => (
                  <td key={tier.id}>
                    <Unset what="Not published" />
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Capabilities</th>
                {TIERS.map((tier) => (
                  <td key={tier.id}>
                    <ul className="mk-caps">
                      {tier.capabilities.map((capability) => (
                        <li key={capability}>{capability}</li>
                      ))}
                    </ul>
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Activities</th>
                {TIERS.map((tier) => (
                  <td key={tier.id}>Every published activity</td>
                ))}
              </tr>
              <tr>
                <th scope="row">At the ceiling</th>
                {TIERS.map((tier) => (
                  <td key={tier.id}>
                    <Unset what="Not published" />
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Buy</th>
                {TIERS.map((tier) => (
                  <td key={tier.id}>
                    <Link to="/sign-in">
                      <Button size="sm">Choose {tier.name}</Button>
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">Which tier your channel lands in</h2>
        <p className="mk-body">
          Between 10 and 25 percent of the people watching take part at the same moment, and the
          rate varies enormously by activity. A vote pulls far more of a channel than a game with
          eight seats.
        </p>
        <Estimator />
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">The same on every tier</h2>
        <dl className="mk-pairs">
          {EVERY_PLAN.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">The eye tracker is a separate purchase</h2>
        <div className="mk-note">
          <p className="mk-body">
            A Tobii Eye Tracker 5 is hardware you buy once, from Tobii, on top of the subscription.
            Activities driven by your eyes need one on your desk and the rest of the catalog does
            not. Where you looked stays on your computer.
          </p>
        </div>
        <div className="mk-cta">
          <Link to="/activities">
            <Button variant="ghost">Browse activities</Button>
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
